import { AppwriteException, Query } from "appwrite";
import { z } from "zod";
import {
	AppwriteClient,
	getAppwriteClient,
	getCurrentUser,
} from "$/modules/appwrite/lib/appwrite";
import { buckets } from "$/modules/appwrite/lib/appwrite-buckets";
import { collections } from "$/modules/appwrite/lib/appwrite-collections";
import type { Questionnaire } from "$/modules/appwrite/types/collections";
import { getTranslations } from "$/modules/i18n/lib/i18n";
import { uploadFile } from "$/modules/storage/lib/storage";
import { decodeTextEscapes } from "$/modules/shared/lib/text-normalization";
import {
	createSharedQuestionnaire,
	deleteSharedQuestionnaire,
	editSharedQuestionnaire,
} from "$/modules/projects/lib/projects";

const getAllQuestionnairesByProjectIdSchema = z.object({
	projectId: z.string(),
});

export const getAllQuestionnairesByProjectId = async (
	data: z.infer<typeof getAllQuestionnairesByProjectIdSchema>,
) => {
	const parsedData = getAllQuestionnairesByProjectIdSchema.parse(data);
	const appwrite = getAppwriteClient();
	const result = await appwrite.tablesDB.listRows<Questionnaire>(
		AppwriteClient.databaseId,
		collections.questionnaires,
		[
			Query.equal("project", parsedData.projectId),
			Query.select(["*", "project.$id", "questions.*"]),
		],
	);
	return {
		...result,
		rows: result.rows.map((questionnaire) => ({
			...questionnaire,
			questions: questionnaire.questions?.map((question) => ({
				...question,
				questionnaire,
			})),
		})),
	};
};

const createQuestionnaireSchema = z.object({
	name: z.string().trim().min(3).max(120),
	previewImage: z.instanceof(File).optional(),
	projectId: z.string(),
});

export const createQuestionnaire = async (data: FormData) => {
	const user = await getCurrentUser();

	const formData = Object.fromEntries(data.entries());
	const parsedData = createQuestionnaireSchema.parse({
		...formData,
		name: decodeTextEscapes(String(formData.name ?? "")),
	});

	const t = await getTranslations("project.questionnaire.message");

	if (!user) throw new Error("Unauthenticated");

	try {
		const file = await uploadFile({
			asset: parsedData.previewImage,
			bucketId: buckets.questionnaires,
		});

		await createSharedQuestionnaire({
			projectId: parsedData.projectId,
			name: parsedData.name,
			previewImage: file,
		});
	} catch (error) {
		if (error instanceof AppwriteException) {
			console.error(
				"Appwrite error while creating questionnaire:",
				error.message,
			);
		}
		throw new Error(t("create.error"));
	}

	return {
		message: t("create.success"),
		success: true,
	};
};

const editQuestionnaireSchema = createQuestionnaireSchema.extend({
	id: z.string(),
	fileId: z.string().optional(),
});

export const editQuestionnaire = async (data: FormData) => {
	await getCurrentUser();
	const formData = Object.fromEntries(data.entries());
	const parsedData = editQuestionnaireSchema.parse({
		...formData,
		name: decodeTextEscapes(String(formData.name ?? "")),
	});

	const t = await getTranslations("project.questionnaire.message");

	const hasNewPreviewImage = Boolean(
		parsedData.previewImage && parsedData.previewImage.size > 0,
	);
	const previewImage = hasNewPreviewImage
		? await uploadFile({
				asset: parsedData.previewImage,
				bucketId: buckets.questionnaires,
			})
		: null;

	await editSharedQuestionnaire({
		questionnaireId: parsedData.id,
		name: parsedData.name,
		previewImage,
		replacePreviewImage: hasNewPreviewImage,
	});

	return {
		message: t("edit.success"),
		success: true,
	};
};

const deleteQuestionnaireSchema = z.object({
	id: z.string(),
});

export const deleteQuestionnaire = async (data: FormData) => {
	await getCurrentUser();
	const parsedData = deleteQuestionnaireSchema.parse(
		Object.fromEntries(data.entries()),
	);
	const t = await getTranslations("project.questionnaire.message");

	await deleteSharedQuestionnaire({ questionnaireId: parsedData.id });

	return {
		message: t("delete.success"),
		success: true,
	};
};
